import { useEffect, useContext } from 'react'
import { Body2White, Body1White } from '@/components/typography'
import { Box, Grid, Button } from '@mui/material'
import { type Inheritor } from '@/store/setUpInheritorsSlice'
import EthHashInfo from '@/components/common/EthHashInfo'

import { encodeRmInheritorFunc } from '@/utils/inherit-manager'
import useChainId from '@/hooks/useChainId'
import { SendProcessingContext } from '@/components/inheritors/SendProcessing'
import { PendingStatus } from '@/store/pendingTxsSlice'
import { ModalButtonContext } from '@/components/common/ModalButton'

const RemoveInheritor = ({
  index,
  inheritor,
  onSendSuccess,
}: {
  inheritor: Inheritor
  index: number
  onSendSuccess: () => void
}) => {
  const { isSending, sendTx, sendError, txStatus } = useContext(SendProcessingContext)
  const { setModalOpen } = useContext(ModalButtonContext)

  const chainId = useChainId()

  useEffect(() => {
    if (isSending) {
      if (txStatus === PendingStatus.INDEXING) {
        onSendSuccess()
        setModalOpen(false)
      }
    }
  }, [txStatus, isSending, sendError])

  const handleConfirm = () => {
    if (inheritor.index !== undefined && inheritor.id !== undefined) {
      const txParams = encodeRmInheritorFunc(chainId, inheritor.address)
      void sendTx(txParams, true)
    }
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Button
              variant="text"
              sx={{
                paddingY: '10px',
                width: '100%',
                borderRadius: '4px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                ':hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            <Button
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
                background: 'var(--Supporting-Carnation, #FF2239)',
                color: 'var(--Supporting-While, #FFF)',
                ':hover': {
                  background: '#FF0E27',
                },
              }}
              onClick={handleConfirm}
            >
              Remove
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }
  return (
    <Box>
      <Body2White my="16px">Are you sure you want to remove this person from your inheritance plan?</Body2White>
      <InheritorInformationType1 index={index} inheritor={inheritor} />
      {sendError !== '' && (
        <Body2White sx={{ color: 'var(--Supporting-Carnation, #FF2239)', marginTop: '8px' }}>{sendError}</Body2White>
      )}
      {renderFooter()}
    </Box>
  )
}

const InheritorInformationType1 = ({ index, inheritor }: { inheritor: Inheritor; index: number }) => {
  return (
    <Grid key={index} item px="16px" py="8px" sx={{ border: '1px solid #2F354B', borderRadius: '6px' }}>
      <Grid container py="2px">
        <Grid item xs={4} sm={2}>
          <Body2White>Name:</Body2White>
        </Grid>
        <Grid item xs={8} sm={10}>
          <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)' }}>
            {inheritor.name || `Inheritor ${index + 1}`}
          </Body2White>
        </Grid>
      </Grid>
      <Grid container py="2px">
        <Grid item xs={4} sm={2}>
          <Body2White>Address:</Body2White>
        </Grid>
        <Grid item xs={8} sm={10}>
          <Body1White sx={{ color: 'var(--Blue-Ribbon-600, #5D5DFF)' }}>
            <EthHashInfo
              address={inheritor.address}
              showAvatar={false}
              showName={false}
              hasExplorer={false}
              shortAddress={false}
              shortenLen={6}
            />
          </Body1White>
        </Grid>
      </Grid>
      <Grid container py="2px">
        <Grid item xs={4} sm={2}>
          <Body2White>Percentage:</Body2White>
        </Grid>
        <Grid item xs={8} sm={10}>
          <Body1White sx={{ color: 'var(--Emerald-600, #22C55E)' }}>{inheritor.percentage}%</Body1White>
        </Grid>
      </Grid>
      <Grid container py="2px">
        <Grid item xs={4} sm={2}>
          <Body2White>Email:</Body2White>
        </Grid>
        <Grid item xs={8} sm={10}>
          <Body2White
            sx={{ color: inheritor.email ? 'var(--Ebony-Clay-300, #D2D7E5)' : 'var(--Ebony-Clay-700, #485581)' }}
          >
            {inheritor.email || '-- empty --'}
          </Body2White>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default RemoveInheritor
